import {IConfigurationService, IDotRezApiSubscriptionKeyHeader} from "./configuration.service.interface";
import {
    ApplicationEnvironmentTypeEnum,
    IConfigurationObject,
    PaymentStrategyEnum,
    PortalTypeEnum
} from "./configuration-object.interface";
import {computed, makeObservable, observable, reaction, runInAction} from "mobx";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {TimeSpan} from "../../types/time-span";
import {NullableString} from "../../types/nullable-types";
import {EnvConfigs} from "./env.configs";
import {SsrsConfiguration} from "./objects/ssrs/ssrs.configuration";
import {AppVersionUpgradeModeEnum} from "../airline-webapi/responses/get-configurations.response";
import {Check} from "../../types/type-checking";
import {RawLocalStorageKeys} from "../storage/raw-local-storage/raw-local-storage-keys";
import {PspCodesEnum} from "../booking/models/payment/methods/psp-codes.enum";
import {InsuranceConfigurationModel} from "./objects/insurance-configuration.models";

const REGULAR_CONFIGS_REFRESH_INTERVAL = TimeSpan.fromMinutes(5);
const ACCELERATED_CONFIGS_REFRESH_INTERVAL = TimeSpan.fromSeconds(5);
const MAINTENANCE_REFRESH_INTERVAL = TimeSpan.fromMinutes(1);

//https://www.youtube.com/watch?v=NJwBNWwNlTk

export class ConfigurationService extends ServiceBase implements IConfigurationService {

    constructor(services: IServiceFactory) {
        super(services);

        this.envConfigs = new EnvConfigs();
        this._isHybridTestModeEnabled = ('true' === this.services.rawLocalStorage.getItem(RawLocalStorageKeys.testModeEnabled));
        const forcedPortalType = this.services.rawLocalStorage.getItem(RawLocalStorageKeys.forcePortalType);
        if(Check.isNullOrUndefined(forcedPortalType)) {
            this._portalType = this.envConfigs.portalType;
        } else {
            this._portalType = parseInt(forcedPortalType);
        }

        if(this._portalType === PortalTypeEnum.Agency) {
            document.title = "Aeroitalia - Agency Portal";
        }

        makeObservable<this, '_portalType'>(this, {
            data: observable,
            _isHybridTestModeEnabled: observable.ref,
            appVersionUpgradeMode: observable.ref,
            ssrs: computed,
            _portalType: observable.ref,
            insurance: computed
        });

        reaction(() => this.services.language.currentLanguage,
            async () => {
                await this.loadConfigurations();
            });

    }

    readonly envConfigs: EnvConfigs;

    get currentEnvironmentType(): ApplicationEnvironmentTypeEnum {
        return this.envConfigs.currentEnvironmentType;
    }

    appVersionUpgradeMode: AppVersionUpgradeModeEnum = AppVersionUpgradeModeEnum.None;

    setPreferredApiOrigin(apiOrigin: string): void {

        apiOrigin = apiOrigin.toLowerCase();
        if(apiOrigin === this.airlineWebapiUrl.toLowerCase()) {
            return;
        }

        const initialApiOrigin = this.envConfigs.getInitialApiOrigin().toLowerCase();
        if(initialApiOrigin === apiOrigin) {
            this.services.rawLocalStorage.removeItem(RawLocalStorageKeys.envConfigsPreferredApiOrigin);
        } else {
            this.services.rawLocalStorage.setItem(RawLocalStorageKeys.envConfigsPreferredApiOrigin, apiOrigin);
        }

        this.services.largeStorageConsumersManager.clearAll();

        this.services.window.reload();
    }

    get airlineWebapiUrl(): string {
        return this.envConfigs.airlineWebapiOrigin;
    }

    get dotRezApiUrl(): string {
        return this.data.dotRezApiUrl;
    }

    get dotRezApiSubscriptionKeyHeader(): IDotRezApiSubscriptionKeyHeader {
        return {
            "Ocp-Apim-Subscription-Key": this.data.dotRezApiSubscriptionKey
        };
    }

    data: IConfigurationObject = {
        b2cBookingEngineUrl: "",
        airlineIataCode: "",
        enforceLoginOnBookingFlow: false,
        websiteHomeUrl: "",
        dotRezApiUrl: "",
        dotRezApiSubscriptionKey: "",
        dotRezSessionTimeout: "00:15:00",
        refreshConfigsInterval: "01:00:00",
        noBundleEarliestCheckIn: "3.00:00:00",
        maxAllowedPassengersNumberOnBooking: 9,
        maximumBagsQuantityPerPassenger: 4,
        enable10KgBag: false,
        enable32KgBag: false,
        enableEckn: false,
        analytics: {
            enableForWebsite: false,
            enableForMobileApp: false,
        },
        enableRandomSeatAllocation: false,
        vouchersRestrictedByPromotions: false,
        hidePricesInSearchMask: false,
        countries: [],
        stations: [],
        macs: [],
        carriers: [],
        italianRegions: [],
        psoMarketsV2: {
            markets: [],
            classesOfService: {
                forResident: [],
                forWorker: [],
                forFamily: []
            }
        },
        supportedCurrencies: [
            {
                "code": "EUR",
                "name": "Euro"
            }
        ],
        systemCodesBlockedForFlightChange: [],
        minTimeToChangeDateBeforeFlight: "04:00:00",
        moveFlight: {
            enabled: false,
            disruptionMinDelayTime: "03:00:00",
            minimumFlightConnectionTime: "00:45:00",
            scheduleRestrictions: {
                daysBeforeFlight: 3,
                daysAfterFlight: 7
            }
        },
        ssrsConfig: {
            flex: {
                bundlesCutOffPeriods: [],
                inAllBundlesPeriods: []
            },
            eckn: {
                bundlesCutOffPeriods: [],
                inAllBundlesPeriods: []
            }
        },
        marketing: {
            enableLowFare: true,
            promotionalMessages: {
                loadingIndicatorTitle: "",
                loadingIndicatorMessage: ""
            }
        },
        translations: {
            errors: {}
        },
        appDownloadLinks: {
            ios: "https://apps.apple.com/us/app/aeroitalia/id6444920474",
            android: "https://play.google.com/store/apps/details?id=com.aeroitalia.mobile.app"
        },
        forbiddenVouchers: [],
        restrictedVouchersByFlightDate: [],
        minDaysToUseRestrictedVoucher: 21,
        blueBenefits: {
            activationUrl: "",
            packages: {}
        },
        payments: {
            strategy: PaymentStrategyEnum.Webapi,
            cardsFallbackPsp: PspCodesEnum.Nuvei,
            savePaymentMethodInfo: false,
            validateCreditCardNumber: true,
            paymentMethods: [],
            statusTimeoutInSeconds: 180,
            abortTimeoutInSeconds: 60,
            googlePay: {
                merchantId: "",
                supportedPlatforms: [],
                supportedOS: []
            },
            applePay: {
                merchantId: "",
                supportedPlatforms: [],
                supportedOS: []
            }
        },
        insurance: {
            enabled: false,
            providers: []
        },
        pspConfigs: {
            PayPal: {
                clientId: "",
                environment: "production",
                enablePayLaterV2: "true"
            },
            Nuvei: {
                merchantId: "",
                merchantSiteId: "",
                environment: "prod",
                googleGateway: "",
                googleGatewayMerchantId: "",
                googleAllowedAuthMethods: "",
                googleAllowedCardNetworks: "",
                acquirerBankCountry: "",
                appleMerchantCapabilities: "supports3DS,supportsEMV,supportsCredit,supportsDebit",
                appleSupportedNetworks: "visa,masterCard,amex",

            },
            Revolut: {
                publicApiKey: "",
                environment: "sandbox",
            },
            EMP: {
                clientEncryptionPublicKey: "",
                useRedirectInIframe: "true"
            }
        },
        virtualSsrsInBundle: {},
        vatPercentagePerCountry: {},
        callCenterPhoneNumber: "",
        companyAddress: "Aeroitalia S.p.A Via Andrea Mantegna 8, 00054, Fiumicino (RM), Italia - P.Iva  16340701008",
        cms: {
            baseUrl: "",
            pages: {
                psoV2: ""
            },
            mobileAppHomePageBanners: []
        },
        bundles: {},
        fees: []
    };
    private _refreshConfigsInterval: any = null;
    private _startRefreshConfigsTimer(refreshInterval?: TimeSpan) {
        if(!refreshInterval) {
            refreshInterval =  this.data.refreshConfigsInterval
                ? TimeSpan.parse(this.data.refreshConfigsInterval)
                : REGULAR_CONFIGS_REFRESH_INTERVAL;
        }


        this._refreshConfigsInterval = setInterval(async () => {
            await this.loadConfigurations();
        }, refreshInterval.totalMilliseconds);
    }

    private _stopRefreshConfigsTimer() {
        if(this._refreshConfigsInterval) {
            clearInterval(this._refreshConfigsInterval);
            this._refreshConfigsInterval = null;
        }
    }

    async loadConfigurations(): Promise<void> {

        this._stopRefreshConfigsTimer();
        try {
            await this.services.maintenance.waitForMaintenance();
            if(this.services.maintenance.data.enableMaintenancePage) {

                try {
                    await this.services.maintenance.refresh();
                } catch (err) {
                    this.services.logger.error('Failed to refresh maintenance data', err);
                } finally {
                    this._startRefreshConfigsTimer(MAINTENANCE_REFRESH_INTERVAL);
                }

            } else {
                const response = await this.services.airlineWebapi.getConfigurations(this.services.language.currentLanguage);

                runInAction(() => {
                    this.data = response.configurations;
                    this.appVersionUpgradeMode = response.appVersionUpgradeMode ?? AppVersionUpgradeModeEnum.None;
                });
                this._startRefreshConfigsTimer();
            }



        } catch (err) {
            this.services.logger.error('Failed to load configurations', err);
            this._startRefreshConfigsTimer(ACCELERATED_CONFIGS_REFRESH_INTERVAL);
        }
    }

    get noBundleEarliestCheckIn(): TimeSpan {
        try {
            return TimeSpan.parse(this.data.noBundleEarliestCheckIn);
        } catch (err) {
            this.services.logger.error(`Failed to parse noBundleEarliestCheckIn ${this.data.noBundleEarliestCheckIn}`, err);
            return TimeSpan.fromDays(3);
        }
    }

    getErrorDescription(errorCode: NullableString): NullableString {
        if(!errorCode) {
            return errorCode;
        }
        if(this.data.translations.errors[errorCode]) {
            return this.data.translations.errors[errorCode]
        } else {
            return errorCode;
        }
    }

    get isProduction(): boolean {
        return this.envConfigs.isProduction;
    }

    get isDevEnv(): boolean {
        return this.currentEnvironmentType === ApplicationEnvironmentTypeEnum.Development;
    }

    get isProductionLike(): boolean {
        return this.isProduction || this.envConfigs.isStage;
    }

    get dotRezSessionTimeout(): TimeSpan {
        return TimeSpan.parse(this.data.dotRezSessionTimeout);
    }

    get ssrs(): SsrsConfiguration {

        if(this.data.ssrsConfig) {
            return new SsrsConfiguration(this.data.ssrsConfig, this.services);
        }

        return new SsrsConfiguration({
            flex: {
                bundlesCutOffPeriods: [],
                inAllBundlesPeriods: []
            },
            eckn: {
                bundlesCutOffPeriods: [],
                inAllBundlesPeriods: []
            }
        }, this.services);
    }


    getBundleVirtualSsrs(bundleCode: string): string[] {
        if(this.data.virtualSsrsInBundle) {
            return this.data.virtualSsrsInBundle[bundleCode] ?? [];
        }

        return [];

    }

    get defaultCountryCode(): string {
        return "IT";
    }

    _isHybridTestModeEnabled: boolean;
    get isHybridTestModeEnabled(): boolean {
        return this._isHybridTestModeEnabled;

    }

    async enableHybridTestMode(password: string): Promise<boolean> {

        const passwordHash = await this.services.crypto.computeHash(password);
        if(passwordHash === "177d58cb7a9bb6f4bcbd5b5173d011fc7edd62790e647474e68e7125e680b397") { //Aeroitalia#123
            this.services.rawLocalStorage.setItem(RawLocalStorageKeys.testModeEnabled, "true");
            runInAction(() => {
                this._isHybridTestModeEnabled = true;
            })
            return true;
        } else {
            this.services.alert.showError("Invalid password");
            return false;
        }

    }

    get  isAnalyticsEnabled(): boolean{
        try {
            return this.services.device.isHybrid ?
                this.data.analytics.enableForMobileApp:
                this.data.analytics.enableForWebsite;
        }
        catch (err) {
            return false;
        }

    }

    get isPayPalPayLaterEnabled(): boolean {
        return this.data.pspConfigs.PayPal?.enablePayLaterV2 === 'true';
    }

    private _portalType: PortalTypeEnum;
    get isAgencyPortal(): boolean {
        return this._portalType === PortalTypeEnum.Agency;
    }

    async switchPortalType(): Promise<void> {
        await this.services.user.logout();
        runInAction(() => {
            if(this.isAgencyPortal) {
                this._portalType = PortalTypeEnum.B2C;
            } else {
                this._portalType = PortalTypeEnum.Agency;
            }
            this.services.rawLocalStorage.setItem(RawLocalStorageKeys.forcePortalType, this._portalType.toString());
        });
    }

    get allowPersistContactInfo(): boolean {
        if(this.envConfigs.currentEnvironmentType === ApplicationEnvironmentTypeEnum.Development
        /*|| this.envConfigs.currentEnvironmentType === ApplicationEnvironmentTypeEnum.Test*/) {
            return true;
        }

        return !this.isAgencyPortal && !this.services.user.isAuthorized;
    }


    get insurance(): InsuranceConfigurationModel {
        return new InsuranceConfigurationModel(this.data.insurance, this.services);
    }

    getFeeTranslation(feeCode: string): NullableString {
        return this.insurance.getFeeTranslation(feeCode);
    }

    shouldShowSsrInShoppingCart(ssrCode: string): boolean {
        return !this.insurance.hasSsrCode(ssrCode);
    }


}

